/**
 * The app's text size: one multiplier, `--text-scale` on `<html>`, that every
 * font size reads. Tailwind's `text-xs..3xl` tokens and the literal pixel sizes
 * in our CSS multiply by it (index.css `@theme`, postcss `scale-font-size`),
 * the AG Grid font and row height follow it (ag-theme-edge.css), and canvas
 * charts read it through `readTextScale()` when they rebuild their theme.
 * Spacing and layout do not change: this scales text, not the interface.
 *
 * Stored as a whole percent (the user profile's `textScale` field) so it reads
 * back from DynamoDB as exactly the value that went in.
 */

// 125% was tried and dropped as too much (Chris 2026-10-07).
export const TEXT_SCALES = [90, 100, 110] as const
export type TextScale = (typeof TEXT_SCALES)[number]

export const DEFAULT_TEXT_SCALE: TextScale = 100

export const TEXT_SCALE_LABELS: Record<TextScale, string> = {
  90: 'Smaller',
  100: 'Normal',
  110: 'Larger',
}

// Mirrored by the pre-paint stamp in index.html.
const STORAGE_KEY = 'edge_text_scale'

export function isTextScale(value: unknown): value is TextScale {
  return typeof value === 'number' && (TEXT_SCALES as readonly number[]).includes(value)
}

export function loadTextScale(): TextScale {
  try {
    const stored = Number(localStorage.getItem(STORAGE_KEY))
    if (isTextScale(stored)) return stored
  } catch {
    // ignore
  }
  return DEFAULT_TEXT_SCALE
}

export function saveTextScale(scale: TextScale): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(scale))
  } catch {
    // ignore quota / privacy-mode failures
  }
}

export function applyTextScale(scale: TextScale, root: HTMLElement): void {
  root.style.setProperty('--text-scale', String(scale / 100))
}

/** The live multiplier (1 = default), for code that sizes text in pixels itself. */
export function readTextScale(): number {
  if (typeof document === 'undefined') return 1
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--text-scale')
  const n = Number.parseFloat(raw)
  return Number.isFinite(n) && n > 0 ? n : 1
}

/**
 * An inline-style font size that follows the setting, for the few places that
 * size text in a `style` object rather than a class (`fontSize: textPx(13)`).
 * Classes and stylesheets need nothing: they scale at build time.
 */
export function textPx(px: number): string {
  return `calc(${px}px * var(--text-scale, 1))`
}
